import type { Mode } from "../input/keys";
import type { Shape, Size } from "../physics/materials";
import type { Material } from "../render/materials";
import { el } from "./dom";
import type { Tooltips } from "./tooltips";

/** What the HUD's buttons and sliders ask the game to do. */
interface HudActions {
  material(i: number): void;
  shape(i: number): void;
  mode(m: Mode): void;
  rotate(): void;
  clear(): void;
  fit(): void;
  top(): void;
  reset(): void;
  act(): void; // the touch Drop / Push / Remove button
  /** The action button was pressed (true) or let go; true when the press was handled. */
  hold(down: boolean): boolean;
}

// Draws an isometric box with the shape's real proportions
function isoIcon([w, h, d]: Size) {
  const P = (x: number, y: number, z: number): [number, number] => [
    0.866 * (x - z),
    0.5 * (x + z) - y,
  ];
  const corners: [number, number][] = [];
  for (const x of [0, w]) for (const y of [0, h]) for (const z of [0, d]) corners.push(P(x, y, z));
  const xs = corners.map((c) => c[0]),
    ys = corners.map((c) => c[1]);
  const minX = Math.min(...xs),
    minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX,
    spanY = Math.max(...ys) - minY;
  const k = 19 / Math.max(spanX, spanY);
  const ox = 12 - (spanX * k) / 2,
    oy = 12 - (spanY * k) / 2;
  const pt = (x: number, y: number, z: number) => {
    const [a, b] = P(x, y, z);
    return `${((a - minX) * k + ox).toFixed(2)},${((b - minY) * k + oy).toFixed(2)}`;
  };
  const face = (pts: Size[], shade: number) =>
    `<polygon points="${pts.map((p) => pt(...p)).join(" ")}" fill="currentColor" fill-opacity="${shade}"/>`;
  return (
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">' +
    face(
      [
        [0, h, 0],
        [w, h, 0],
        [w, h, d],
        [0, h, d],
      ],
      0.45,
    ) + // top
    face(
      [
        [0, 0, d],
        [w, 0, d],
        [w, h, d],
        [0, h, d],
      ],
      0.2,
    ) + // left side
    face(
      [
        [w, 0, 0],
        [w, 0, d],
        [w, h, d],
        [w, h, 0],
      ],
      0.05,
    ) + // right side
    "</svg>"
  );
}

/** Marks button `i` of a picker as the pressed one. */
function pressOnly(container: HTMLElement, i: number) {
  [...container.children].forEach((b, k) => b.setAttribute("aria-pressed", String(k === i)));
}

/* =====================================================================
   HUD: material and shape pickers, tools, sliders, score
   ===================================================================== */
export function initHud(
  materials: readonly Material[],
  shapes: readonly Shape[],
  tips: Tooltips,
  on: HudActions,
) {
  const matsEl = el("mats");
  materials.forEach((m, i) => {
    const b = document.createElement("button");
    b.className = "mat";
    b.setAttribute("aria-pressed", String(i === 0));
    b.title = `${m.name} (key ${i + 1})`;
    b.innerHTML = `<span class="swatch" style="background:url(${(m.tex.image as HTMLCanvasElement).toDataURL()}) center/cover"></span>
    <span><span class="mat-name">${m.name}</span><br><span class="mat-trait">${m.trait}</span></span>`;
    b.addEventListener("click", () => on.material(i));
    matsEl.appendChild(b);
  });

  const shapesEl = el("shapes");
  shapes.forEach((s, i) => {
    const b = document.createElement("button");
    b.className = "shape shape-icon";
    b.innerHTML = isoIcon(s.size);
    b.dataset.tip = s.name;
    b.setAttribute("aria-label", s.name);
    b.setAttribute("aria-pressed", String(i === 0));
    b.addEventListener("click", () => on.shape(i));
    shapesEl.appendChild(b);
  });

  el("rotate").addEventListener("click", on.rotate);
  el("clear").addEventListener("click", on.clear);
  el("vFit").addEventListener("click", on.fit);
  el("vTop").addEventListener("click", on.top);
  el("vReset").addEventListener("click", on.reset);

  /* ---------- Touch action button and collapsible toolbar ---------- */
  const actBtn = el<HTMLButtonElement>("actBtn");
  const trayBox = el("tray");
  // Holding Drop keeps dropping, so a press acts at once instead of on release.
  // The click that follows it is then skipped; a keyboard click (detail 0) still acts
  let pressHandled = false;
  actBtn.addEventListener("pointerdown", (e) => {
    pressHandled = e.button === 0 && on.hold(true);
  });
  for (const type of ["pointerup", "pointercancel", "pointerleave"])
    actBtn.addEventListener(type, () => on.hold(false));
  actBtn.addEventListener("click", (e) => {
    if (pressHandled && e.detail > 0) pressHandled = false;
    else on.act();
  });
  actBtn.addEventListener("contextmenu", (e) => e.preventDefault()); // a long press is a hold
  new ResizeObserver(() => {
    actBtn.style.bottom = `calc(${trayBox.offsetHeight + 22}px + env(safe-area-inset-bottom, 0px))`;
  }).observe(trayBox);

  const foldBtn = el("fold");
  function setFolded(f: boolean) {
    trayBox.classList.toggle("collapsed", f);
    const label = f ? "More options" : "Fewer options";
    // Chevron points up to expand the tray, down to fold it away
    foldBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${f ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"}"/></svg>`;
    foldBtn.dataset.tip = label;
    foldBtn.setAttribute("aria-label", label);
    foldBtn.setAttribute("aria-expanded", String(!f));
    // A tip showing for the button follows the new label. Not ":hover": after a
    // tap that matches too, and would open a tip that nothing closes
    tips.refresh(foldBtn);
  }
  foldBtn.addEventListener("click", () => setFolded(!trayBox.classList.contains("collapsed")));
  setFolded(innerWidth < 600);

  /* ---------- Tools and their settings ---------- */
  let pushSpeed = 3;
  const toolsEl = el("tools");
  const dropCtl = el("dropCtl");
  const pushCtl = el("pushCtl");
  const pushInput = el<HTMLInputElement>("pushS");
  const pushLabel = el("pushLabel");
  pushInput.addEventListener("input", () => {
    pushSpeed = parseFloat(pushInput.value);
    pushLabel.textContent = String(pushSpeed);
  });
  toolsEl
    .querySelectorAll("button")
    .forEach((b) => b.addEventListener("click", () => on.mode(b.dataset.mode as Mode)));

  /* ---------- Scoring display ---------- */
  // Only the height shows; a tap on it opens a card with the other scores
  const scoreBtn = el("score");
  const statsEl = el("stats");
  const hEl = el("h");
  const bestEl = el("best");
  const countEl = el("count");
  const fallenEl = el("fallen");
  function setStatsOpen(open: boolean) {
    statsEl.hidden = !open;
    scoreBtn.setAttribute("aria-expanded", String(open));
  }
  scoreBtn.addEventListener("click", () => setStatsOpen(statsEl.hidden));
  // A press anywhere else closes the card. On the scene that press only closes
  // it: the canvas never sees it, so it neither aims nor drops a block
  addEventListener(
    "pointerdown",
    (e) => {
      if (statsEl.hidden || scoreBtn.contains(e.target as Node)) return;
      if (statsEl.contains(e.target as Node)) return;
      setStatsOpen(false);
      if (e.target instanceof HTMLCanvasElement) e.stopPropagation();
    },
    { capture: true },
  );
  addEventListener("keydown", (e) => {
    if (e.key === "Escape") setStatsOpen(false);
  });
  // The height flashes gold when the best it would show goes up; the best
  // creeps by hundredths as a tower settles, which mustn't flash it again
  let bestText = "";
  scoreBtn.addEventListener("animationend", () => scoreBtn.classList.remove("new-best"));

  return {
    pushSpeed: () => pushSpeed,
    showMaterial: (i: number) => pressOnly(matsEl, i),
    showShape: (i: number) => pressOnly(shapesEl, i),
    showMode(m: Mode) {
      toolsEl
        .querySelectorAll("button")
        .forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.mode === m)));
      trayBox.classList.toggle("mode-other", m !== "place");
      dropCtl.classList.toggle("hidden", m !== "place");
      pushCtl.classList.toggle("hidden", m !== "push");
    },
    showActButton(mode: Mode, touch: boolean, hasSelection: boolean) {
      actBtn.classList.toggle("hidden", !touch || mode === "grab");
      if (mode === "place") {
        actBtn.textContent = "Drop";
        actBtn.disabled = false;
      } else {
        actBtn.textContent = mode === "push" ? "Push" : "Remove";
        actBtn.disabled = !hasSelection;
      }
    },
    showScore(height: number, best: number, placed: number, fallen: number) {
      hEl.textContent = height.toFixed(1);
      const text = best.toFixed(1) + " m";
      if (text !== bestText) {
        // the first call shows the saved best, which is no new record
        if (bestText) scoreBtn.classList.add("new-best");
        bestText = bestEl.textContent = text;
      }
      countEl.textContent = String(placed);
      fallenEl.textContent = String(fallen);
    },
  };
}
